import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, LinkIcon } from "lucide-react";

function ConfigPanel({ companyId, catalog, onReload }) {
  const [valorFijoId, setValorFijoId] = useState(catalog.custom_field_valor_fijo_id || "");
  const [referenciaId, setReferenciaId] = useState(catalog.custom_field_referencia_id || "");
  const [termId, setTermId] = useState(catalog.term_net30_id || "");
  // P32 (2026-09-22): firma de la EMPRESA al pie de la nota al cliente.
  const [firmaNota, setFirmaNota] = useState(catalog.firma_nota || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/quickbooks/config/${companyId}`, {
        custom_field_valor_fijo_id: valorFijoId || null,
        custom_field_referencia_id: referenciaId || null,
        term_net30_id: termId || null,
        // Sin "|| null": la cadena vacía es cómo se borra la firma y vuelve a firmarse con el
        // nombre del usuario que emite.
        firma_nota: firmaNota,
      });
      toast.success("Configuración de facturación guardada");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="qbo-config-panel">
      {catalog.simulado && (
        <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="qbo-config-simulado-note">
          Sin conexión real todavía — este catálogo es de ejemplo (10 clientes y 10 rutas ficticios) para poder
          probar mapeos y facturación en modo simulación. Cuando conectes credenciales reales de Intuit, este
          catálogo se reemplaza solo por el real.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          {/* P31 (2026-09-21): en las facturas reales de Cynthia ALVAREZ2025 va vacío: ya no se escribe. */}
          <Label className="text-xs text-muted-foreground">Campo ALVAREZ2025 (no se escribe — va vacío)</Label>
          <Select value={valorFijoId} onValueChange={setValorFijoId}>
            <SelectTrigger className="mt-1" data-testid="qbo-config-valor-fijo"><SelectValue placeholder="Elegir campo" /></SelectTrigger>
            <SelectContent>
              {catalog.custom_fields.map((cf) => <SelectItem key={cf.DefinitionId} value={cf.DefinitionId}>{cf.Name} (Id {cf.DefinitionId})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Campo personalizado — REFERENCIA del cliente (obligatorio)</Label>
          <Select value={referenciaId} onValueChange={setReferenciaId}>
            <SelectTrigger className="mt-1" data-testid="qbo-config-referencia"><SelectValue placeholder="Elegir campo" /></SelectTrigger>
            <SelectContent>
              {catalog.custom_fields.map((cf) => <SelectItem key={cf.DefinitionId} value={cf.DefinitionId}>{cf.Name} (Id {cf.DefinitionId})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Término de pago Net 30</Label>
          <Select value={termId} onValueChange={setTermId}>
            <SelectTrigger className="mt-1" data-testid="qbo-config-term"><SelectValue placeholder="Elegir término" /></SelectTrigger>
            <SelectContent>
              {catalog.terms.map((t) => <SelectItem key={t.Id} value={t.Id}>{t.Name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        {/* P32 (2026-09-22): antes la nota se firmaba con el nombre del usuario que apretaba el
            botón — en una prueba salió "MELINDA (DUEÑO)". Las facturas reales van siempre firmadas
            igual, sin importar quién las emite. */}
        <Label className="text-xs text-muted-foreground">Firma al pie de la nota al cliente</Label>
        <Input className="mt-1 max-w-sm" value={firmaNota} maxLength={80} placeholder="Ej.: CYNTHIA SALDAÑA"
          onChange={(e) => setFirmaNota(e.target.value)} data-testid="qbo-config-firma" />
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="qbo-config-firma-nota">
          Se escribe en mayúsculas al final de la nota "SE RECOLECTO EN / SE ENTREGA EN". Si lo dejás
          vacío, cada factura se firma con el nombre del usuario que la emite.
        </p>
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="qbo-config-save">Guardar configuración</Button>
      <p className="text-xs text-muted-foreground" data-testid="qbo-tags-note">
        Los "Tags" de QuickBooks no se pueden escribir por la API pública — el número de viaje queda en las
        notas internas (PrivateNote) de la factura y visible en la línea "V: ..." de las líneas.
      </p>
    </div>
  );
}

function SugerenciasPanel({ companyId, onAplicado }) {
  const [data, setData] = useState(null);
  const [marcados, setMarcados] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data: d } = await api.get("/quickbooks/mapeos/sugerencias", { params: { company_id: companyId } });
      setData(d);
      const m = {};
      d.clientes.forEach((c) => { m[`c:${c.client_id}`] = true; });
      d.rutas.forEach((r) => { m[`r:${r.ruta_texto}`] = true; });
      setMarcados(m);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async () => {
    setGuardando(true);
    try {
      const { data: res } = await api.post("/quickbooks/mapeos/confirmar", {
        company_id: companyId,
        clientes: data.clientes.filter((c) => marcados[`c:${c.client_id}`]).map((c) => ({
          client_id: c.client_id, qbo_customer_id: c.qbo_customer_id, qbo_customer_name: c.qbo_customer_name })),
        rutas: data.rutas.filter((r) => marcados[`r:${r.ruta_texto}`]).map((r) => ({
          ruta_texto: r.ruta_texto, qbo_item_id: r.qbo_item_id, qbo_item_name: r.qbo_item_name })),
      });
      toast.success(`${res.clientes_mapeados} cliente(s) y ${res.rutas_mapeadas} ruta(s) mapeados`);
      if (res.omitidos?.length) toast.info(res.omitidos.join(" · "));
      await cargar();
      onAplicado();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGuardando(false); }
  };

  if (!data) return null;
  const total = data.clientes.length + data.rutas.length;
  const elegidos = Object.values(marcados).filter(Boolean).length;
  const filas = [
    ...data.clientes.map((c) => ({ key: `c:${c.client_id}`, tipo: "Cliente", nombre: c.nombre, destino: c.qbo_customer_name, score: c.score })),
    ...data.rutas.map((r) => ({ key: `r:${r.ruta_texto}`, tipo: "Ruta", nombre: r.ruta_texto, destino: r.qbo_item_name, score: r.score })),
  ];

  return (
    <div className="space-y-3" data-testid="qbo-sugerencias-panel">
      <p className="rounded-md bg-slate-50 p-3 text-xs text-muted-foreground" data-testid="qbo-sugerencias-resumen">
        Coincidencias inequívocas entre lo que hay en la plataforma y el catálogo de QuickBooks. Revisá la lista y
        confirmá: nada se guarda hasta que toques el botón, y lo que ya mapeaste a mano no se pisa nunca. Quedan
        para elegir a mano {data.resumen.clientes_a_mano} cliente(s) y {data.resumen.rutas_a_mano} ruta(s) —
        siglas como "CHR" o ciudades que existen en dos estados no se proponen a propósito.
      </p>
      {data.simulado && (
        <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="qbo-sugerencias-simulado">
          El catálogo todavía es el de ejemplo. Cuando se conecte la cuenta real de QuickBooks, volvé a esta
          pestaña: las sugerencias se recalculan contra los clientes y las rutas reales.
        </p>
      )}
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>En la plataforma</TableHead>
              <TableHead>Se mapea a</TableHead>
              <TableHead className="text-right">Parecido</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.key} data-testid={`qbo-sugerencia-row-${f.key}`}>
                <TableCell>
                  <Checkbox checked={!!marcados[f.key]} data-testid={`qbo-sugerencia-check-${f.key}`}
                    onCheckedChange={() => setMarcados((m) => ({ ...m, [f.key]: !m[f.key] }))} />
                </TableCell>
                <TableCell className="text-sm">
                  <Badge variant="outline" className="mr-2 text-[10px]">{f.tipo}</Badge>{f.nombre}
                </TableCell>
                <TableCell className="text-sm">{f.destino}</TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">{Math.round(f.score * 100)}%</TableCell>
              </TableRow>
            ))}
            {total === 0 && (
              <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground" data-testid="qbo-sugerencias-vacio">
                No hay coincidencias inequívocas pendientes.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Button size="sm" onClick={confirmar} disabled={guardando || elegidos === 0} data-testid="qbo-sugerencias-confirmar">
        Confirmar {elegidos} mapeo(s)
      </Button>
    </div>
  );
}

// P32 (2026-09-22): etiquetas con las que los clientes nombran su referencia en la factura.
// En las facturas reales: Clover lleva "LOAD # 15818" y XPO "PRO # 2612951". Sin elegir nada
// se usa LOAD, que es el comportamiento que ya tenía el backend desde P31.
const ETIQUETAS_REFERENCIA = ["LOAD", "PRO", "REF", "BOL", "PO"];

// P32 (2026-09-22): la cuenta real de QuickBooks tiene 1.697 items y 459 clientes. El <Select>
// anterior los pintaba todos, sin buscador y sin orden útil: era imposible de usar. Acá se filtra
// a mano (cmdk con shouldFilter apagado, para no correr su scoring sobre 1.697 filas en cada
// tecla) y se pintan como mucho MAX_RESULTADOS: con dos letras ya alcanza para encontrar.
const MAX_RESULTADOS = 50;

function BuscadorCatalogo({ opciones, campoLabel, value, onChange, placeholder, testId, ancho = "w-64" }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const lista = opciones || [];
  const elegido = lista.find((o) => o.Id === value);
  const texto = busqueda.trim().toUpperCase();
  const coincidencias = texto ? lista.filter((o) => (o[campoLabel] || "").toUpperCase().includes(texto)) : lista;
  const visibles = coincidencias.slice(0, MAX_RESULTADOS);

  const abrirCerrar = (v) => { setAbierto(v); if (!v) setBusqueda(""); };
  const elegir = (id) => { onChange(id); setAbierto(false); setBusqueda(""); };

  return (
    <Popover open={abierto} onOpenChange={abrirCerrar}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={abierto}
          className={`${ancho} justify-between font-normal`} data-testid={testId}>
          <span className="truncate">{elegido ? elegido[campoLabel] : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Escribí para buscar…" value={busqueda} onValueChange={setBusqueda}
            data-testid={`${testId}-buscar`} />
          <CommandList>
            <CommandEmpty data-testid={`${testId}-vacio`}>Nada coincide con esa búsqueda.</CommandEmpty>
            <CommandGroup>
              {visibles.map((o) => (
                <CommandItem key={o.Id} value={o.Id} onSelect={() => elegir(o.Id)}
                  data-testid={`${testId}-opcion-${o.Id}`}>
                  <Check className={`mr-2 h-4 w-4 ${o.Id === value ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{o[campoLabel]}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {coincidencias.length > visibles.length && (
            <p className="border-t px-3 py-2 text-[11px] text-muted-foreground" data-testid={`${testId}-mas`}>
              {coincidencias.length} coincidencias — se muestran las primeras {MAX_RESULTADOS}. Escribí un poco más.
            </p>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MapTable({ title, rows, catalogo, catalogoKey, catalogoLabel, onSave, testPrefix, conEtiqueta = false }) {
  const [drafts, setDrafts] = useState({});
  const [etiquetas, setEtiquetas] = useState({});

  const rowKey = (r) => r.client_id || r.ruta_texto;

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{title}</TableHead>
            <TableHead>Mapeo en QuickBooks</TableHead>
            {conEtiqueta && <TableHead className="w-36">Referencia</TableHead>}
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const key = rowKey(r);
            const draft = drafts[key] ?? r[catalogoKey + "_id"] ?? "";
            const etiqueta = etiquetas[key] ?? r.etiqueta_referencia ?? "LOAD";
            return (
              <TableRow key={key} data-testid={`${testPrefix}-row-${key}`}>
                <TableCell className="max-w-[220px] truncate text-sm">
                  {r.client_nombre || r.ruta_texto}
                  {!r.mapeado && r.sugerencias?.length > 0 && (
                    <p className="mt-0.5 text-[11px] text-amber-700">Sugerencia: {r.sugerencias[0][catalogoLabel]}</p>
                  )}
                </TableCell>
                <TableCell>
                  <BuscadorCatalogo opciones={catalogo} campoLabel={catalogoLabel} value={draft}
                    onChange={(v) => setDrafts((d) => ({ ...d, [key]: v }))} placeholder="Sin mapear"
                    testId={`${testPrefix}-select-${key}`} />
                </TableCell>
                {conEtiqueta && (
                  <TableCell>
                    <Select value={etiqueta} onValueChange={(v) => setEtiquetas((e) => ({ ...e, [key]: v }))}>
                      <SelectTrigger className="w-32" data-testid={`${testPrefix}-etiqueta-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ETIQUETAS_REFERENCIA.map((et) => <SelectItem key={et} value={et}>{et} #</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                )}
                <TableCell className="text-right">
                  {r.mapeado ? <Badge className="bg-green-50 text-green-700 border border-green-200">Mapeado</Badge>
                             : <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Falta mapear</Badge>}
                  <Button size="sm" variant="outline" className="ml-2" disabled={!draft}
                    onClick={() => onSave(r, draft, catalogo.find((c) => c.Id === draft), etiqueta)}
                    data-testid={`${testPrefix}-save-${key}`}>
                    Guardar
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={conEtiqueta ? 4 : 3} className="py-6 text-center text-muted-foreground">Nada para mapear todavía.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function QuickBooksMapeosCard({ companyId, companyNombre }) {
  const [catalog, setCatalog] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [rutas, setRutas] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: cfg }, { data: cl }, { data: rt }] = await Promise.all([
        api.get(`/quickbooks/config/${companyId}`),
        api.get("/quickbooks/mapeos/clientes", { params: { company_id: companyId } }),
        api.get("/quickbooks/mapeos/rutas", { params: { company_id: companyId } }),
      ]);
      setCatalog(cfg);
      setClientes(cl.items);
      setRutas(rt.items);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const saveCliente = async (r, qboId, qboObj, etiqueta) => {
    try {
      await api.put(`/quickbooks/mapeos/clientes/${r.client_id}`, {
        company_id: companyId, qbo_customer_id: qboId, qbo_customer_name: qboObj?.DisplayName || qboId,
        // P32 (2026-09-22): cómo se llama la referencia de ESTE cliente en la factura.
        etiqueta_referencia: etiqueta || "LOAD",
      });
      toast.success("Cliente mapeado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveRuta = async (r, qboId, qboObj) => {
    try {
      await api.put("/quickbooks/mapeos/rutas", {
        company_id: companyId, ruta_texto: r.ruta_texto, qbo_item_id: qboId, qbo_item_name: qboObj?.Name || qboId,
      });
      toast.success("Ruta mapeada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!catalog) return null;

  return (
    <Card data-testid="qbo-mapeos-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading">
          <LinkIcon className="h-5 w-5 text-primary" /> Mapeos QuickBooks — {companyNombre}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-xs text-muted-foreground">
          Solo aplica a Álvarez (Trébol factura por su propio sistema CFDI). Si falta un mapeo, la pantalla de
          Facturación QuickBooks no deja crear la factura hasta que lo completes acá.
        </p>
        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config" data-testid="qbo-mapeos-tab-config">Configuración</TabsTrigger>
            <TabsTrigger value="sugerencias" data-testid="qbo-mapeos-tab-sugerencias">Sugerencias</TabsTrigger>
            <TabsTrigger value="clientes" data-testid="qbo-mapeos-tab-clientes">Clientes ({clientes.filter((c) => !c.mapeado).length} sin mapear)</TabsTrigger>
            <TabsTrigger value="rutas" data-testid="qbo-mapeos-tab-rutas">Rutas ({rutas.filter((r) => !r.mapeado).length} sin mapear)</TabsTrigger>
          </TabsList>
          <TabsContent value="config" className="mt-4">
            <ConfigPanel companyId={companyId} catalog={catalog} onReload={load} />
          </TabsContent>
          <TabsContent value="sugerencias" className="mt-4">
            <SugerenciasPanel companyId={companyId} onAplicado={load} />
          </TabsContent>
          <TabsContent value="clientes" className="mt-4 space-y-3">
            {/* P32 (2026-09-22): la columna Referencia decide cómo arranca esa línea en la factura. */}
            <p className="rounded-md bg-slate-50 p-3 text-xs text-muted-foreground" data-testid="qbo-map-cliente-nota">
              La columna <strong>Referencia</strong> es cómo llama ese cliente a su número en la factura:
              Clover usa "LOAD # 15818" y XPO "PRO # 2612951". Se guarda junto con el mapeo, con el mismo
              botón Guardar de la fila. Si no elegís nada, se usa LOAD.
            </p>
            <MapTable title="Cliente en la plataforma" rows={clientes} catalogo={catalog.customers}
              catalogoKey="qbo_customer" catalogoLabel="DisplayName" onSave={saveCliente} testPrefix="qbo-map-cliente"
              conEtiqueta />
          </TabsContent>
          <TabsContent value="rutas" className="mt-4">
            <MapTable title="Ruta observada en Viajes" rows={rutas} catalogo={catalog.items}
              catalogoKey="qbo_item" catalogoLabel="Name" onSave={saveRuta} testPrefix="qbo-map-ruta" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
